import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Space, Tag, Typography } from "antd";
import { loadTianditu } from "../../utils/tianditu";
import {
  getBoundaryCenter,
  normalizeBoundary,
  prepareBoundaryForSave,
} from "../../utils/boundary";
import { Locale } from "../../utils";
import { TIANDITU_ZOOM } from "../../constants";

const i18n = Locale.getInstance();
const { Text } = Typography;

function DeliveryZoneBoundaryMap({
  boundary,
  onChange,
  shopLongitude,
  shopLatitude,
  disabled = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const clickHandlerRef = useRef(null);
  const drawingPointsRef = useRef([]);

  const [ready, setReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);

  const clearDrawingArtifacts = useCallback(() => {
    if (!mapRef.current) return;
    const { map } = mapRef.current;

    if (polylineRef.current) {
      map.removeOverLay(polylineRef.current);
      polylineRef.current = null;
    }

    markersRef.current.forEach((marker) => map.removeOverLay(marker));
    markersRef.current = [];
    drawingPointsRef.current = [];
  }, []);

  const clearPolygon = useCallback(() => {
    if (!mapRef.current) return;
    if (polygonRef.current) {
      mapRef.current.map.removeOverLay(polygonRef.current);
      polygonRef.current = null;
    }
  }, []);

  const renderPolygon = useCallback(
    (points) => {
      if (!mapRef.current || !points || points.length < 3) {
        clearPolygon();
        return;
      }

      const { map, T } = mapRef.current;
      clearPolygon();
      clearDrawingArtifacts();

      const lngLats = points.map((point) => new T.LngLat(point[0], point[1]));
      polygonRef.current = new T.Polygon(lngLats, {
        color: "#1677ff",
        weight: 3,
        opacity: 0.9,
        fillColor: "#1677ff",
        fillOpacity: 0.18,
      });
      map.addOverLay(polygonRef.current);

      try {
        const bounds = polygonRef.current.getBounds();
        if (bounds && typeof map.setViewport === "function") {
          map.setViewport([bounds.getSouthWest(), bounds.getNorthEast()]);
        }
      } catch (e) {
        // ignore viewport errors on older Tianditu builds
      }
    },
    [clearDrawingArtifacts, clearPolygon]
  );

  useEffect(() => {
    let cancelled = false;

    loadTianditu()
      .then((T) => {
        if (cancelled || !containerRef.current) return;

        const map = new T.Map(containerRef.current);
        const fallback = {
          longitude: parseFloat(shopLongitude) || 87.68,
          latitude: parseFloat(shopLatitude) || 43.77,
        };
        const center = getBoundaryCenter(boundary, fallback);
        map.centerAndZoom(
          new T.LngLat(center.longitude, center.latitude),
          TIANDITU_ZOOM || 14
        );

        if (shopLongitude && shopLatitude) {
          const shopMarker = new T.Marker(
            new T.LngLat(parseFloat(shopLongitude), parseFloat(shopLatitude))
          );
          map.addOverLay(shopMarker);
        }

        mapRef.current = { map, T };
        setReady(true);
      })
      .catch((error) => {
        console.error("DeliveryZoneBoundaryMap init failed", error);
      });

    return () => {
      cancelled = true;
      if (clickHandlerRef.current && mapRef.current?.map) {
        mapRef.current.map.removeEventListener("click", clickHandlerRef.current);
      }
      if (mapRef.current?.map) {
        mapRef.current.map.clearOverLays();
      }
      mapRef.current = null;
      polygonRef.current = null;
      polylineRef.current = null;
      markersRef.current = [];
      setReady(false);
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const points = normalizeBoundary(boundary);
    if (points?.length >= 3 && !isDrawing) {
      renderPolygon(points);
    } else if (!isDrawing) {
      clearPolygon();
    }
  }, [boundary, ready, isDrawing, renderPolygon, clearPolygon]);

  const startDrawing = () => {
    if (!mapRef.current || disabled) return;
    const { map, T } = mapRef.current;

    setIsDrawing(true);
    onChange(null);
    clearPolygon();
    clearDrawingArtifacts();

    if (clickHandlerRef.current) {
      map.removeEventListener("click", clickHandlerRef.current);
    }

    const clickHandler = (event) => {
      const lng = event.lnglat.getLng();
      const lat = event.lnglat.getLat();
      drawingPointsRef.current.push([lng, lat]);

      const marker = new T.Marker(new T.LngLat(lng, lat));
      map.addOverLay(marker);
      markersRef.current.push(marker);

      if (drawingPointsRef.current.length > 1) {
        if (polylineRef.current) {
          map.removeOverLay(polylineRef.current);
        }
        const linePoints = drawingPointsRef.current.map(
          (point) => new T.LngLat(point[0], point[1])
        );
        polylineRef.current = new T.Polyline(linePoints, {
          color: "#16a34a",
          weight: 2,
          opacity: 0.9,
        });
        map.addOverLay(polylineRef.current);
      }
    };

    clickHandlerRef.current = clickHandler;
    map.addEventListener("click", clickHandler);
  };

  const finishDrawing = () => {
    const points = drawingPointsRef.current;
    if (!points || points.length < 3) {
      window.alert(i18n.t("deliveryZone.minPoints"));
      return;
    }

    if (clickHandlerRef.current && mapRef.current?.map) {
      mapRef.current.map.removeEventListener("click", clickHandlerRef.current);
      clickHandlerRef.current = null;
    }

    const saved = prepareBoundaryForSave(points);
    onChange(saved);
    setIsDrawing(false);
    clearDrawingArtifacts();
    renderPolygon(saved);
  };

  const clearBoundary = () => {
    if (disabled) return;

    if (clickHandlerRef.current && mapRef.current?.map) {
      mapRef.current.map.removeEventListener("click", clickHandlerRef.current);
      clickHandlerRef.current = null;
    }

    setIsDrawing(false);
    clearDrawingArtifacts();
    clearPolygon();
    onChange(null);
  };

  const pointCount = normalizeBoundary(boundary)?.length || 0;

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Button type="primary" onClick={startDrawing} disabled={disabled || isDrawing}>
          {i18n.t("deliveryZone.startDrawing")}
        </Button>
        <Button onClick={finishDrawing} disabled={disabled || !isDrawing}>
          {i18n.t("deliveryZone.finishDrawing")}
        </Button>
        <Button danger onClick={clearBoundary} disabled={disabled}>
          {i18n.t("deliveryZone.clear")}
        </Button>
        <Tag>
          {i18n.t("deliveryZone.pointCount", { count: pointCount })}
        </Tag>
      </Space>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {isDrawing
          ? i18n.t("deliveryZone.drawingHint")
          : i18n.t("deliveryZone.idleHint")}
      </Text>
      <div
        ref={containerRef}
        id="delivery-zone-map"
        style={{ width: "100%", height: 360, border: "1px solid #f0f0f0" }}
      />
    </div>
  );
}

export default DeliveryZoneBoundaryMap;
